import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Blade from '@/components/ui/Blade/Blade';
import Button from '@/components/ui/Button';
import { formatDateTimeLocale } from '@/helpers/dateHelper';
import { WORLDS, levelIn } from '@/helpers/permissionWorlds';
import { CoordinatorAddRequestDtoOut, asUtc } from '@/models/out/CoordinatorAddRequestDtoOut';
import styles from '../FundsManagement/FundBlade/FundBlade.module.css';

const PREFIX = 'coordinatorAddRequests';

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className={styles.personMeta}>{label}</span>
    <span className="body-sm-semibold break-words">{children}</span>
  </div>
);

interface CoordinatorAddRequestBladeProps {
  request: CoordinatorAddRequestDtoOut;
  onClose: () => void;
  onApprove: (request: CoordinatorAddRequestDtoOut) => void;
  onReject: (request: CoordinatorAddRequestDtoOut) => void;
}

/**
 * A request's card (HAT-10552, 3.11.2026 - the office asked for a window with all of a row's details
 * on clicking it): the coordinator the lead coordinator asked to add, the project, the permission
 * world by world, and who asked and when - with the same approve and reject as the row, on top, so
 * they are there without scrolling. The copy of the coordinator's ID card the lead coordinator
 * attached opens from here; approving files it in the project's documents, rejecting destroys it.
 */
const CoordinatorAddRequestBlade = ({ request, onClose, onApprove, onReject }: CoordinatorAddRequestBladeProps) => {
  const { t } = useTranslation();
  const name = `${request.firstName ?? ''} ${request.lastName ?? ''}`.trim() || request.email;

  const headerTop = <span className="body-lr-semibold text-(--font-body-4)">{t(`${PREFIX}.bladeTitle`, { name })}</span>;

  return (
    <Blade handleXclick={onClose} headerTop={headerTop} headerBottom={<></>} width="520px">
      <div className={`${styles.content} custom-scrollbar px-6 pb-6`}>
        {request.status === 'Pending' && (
          <div className="flex flex-wrap gap-2 pb-4">
            <Button variant="primary" onClick={() => onApprove(request)}>
              {t(`${PREFIX}.approve`)}
            </Button>
            <Button variant="outline" onClick={() => onReject(request)}>
              {t(`${PREFIX}.reject`)}
            </Button>
          </div>
        )}
        <section className="flex flex-col gap-4">
          <h3 className={styles.sectionTitle}>{t(`${PREFIX}.coordinator`)}</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t(`${PREFIX}.fields.name`)}>{name}</Field>
            <Field label={t(`${PREFIX}.fields.israeliId`)}>
              <bdi>{request.israeliId}</bdi>
            </Field>
            <Field label={t(`${PREFIX}.fields.email`)}>
              <bdi dir="ltr">{request.email}</bdi>
            </Field>
            <Field label={t(`${PREFIX}.fields.phone`)}>
              <bdi dir="ltr">{request.phone}</bdi>
            </Field>
            <Field label={t(`${PREFIX}.fields.city`)}>{request.city}</Field>
            <div className="col-span-2">
              <Field label={t(`${PREFIX}.fields.idCopy`)}>
                {request.idDocuments && request.idDocuments.length > 0 ? (
                  <span className="flex flex-col gap-1">
                    {request.idDocuments.map((doc, index) => (
                      <a key={`${doc.uri}-${index}`} href={doc.uri} target="_blank" rel="noopener noreferrer" className="underline">
                        <bdi dir="ltr">{doc.name}</bdi>
                      </a>
                    ))}
                  </span>
                ) : (
                  t(`${PREFIX}.fields.idCopyMissing`)
                )}
              </Field>
            </div>
          </div>
        </section>

        <div className={styles.line} />
        <section className="flex flex-col gap-4">
          <h3 className={styles.sectionTitle}>{t(`${PREFIX}.fund`)}</h3>
          <Field label={t(`${PREFIX}.fields.fund`)}>{request.fundName ? `${request.fundName} (${request.fundId})` : request.fundId}</Field>
        </section>

        <div className={styles.line} />
        <section className="flex flex-col gap-4">
          <h3 className={styles.sectionTitle}>{t(`${PREFIX}.permission`)}</h3>
          <div className="grid grid-cols-3 gap-4">
            {WORLDS.map((world) => (
              <Field key={world} label={t(`permissionWorlds.names.${world}`)}>
                {t(`permissionWorlds.levels.${levelIn(request.worlds, world)}`)}
              </Field>
            ))}
          </div>
        </section>

        <div className={styles.line} />
        <section className="flex flex-col gap-4">
          <h3 className={styles.sectionTitle}>{t(`${PREFIX}.requestedBy`)}</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t(`${PREFIX}.fields.name`)}>{request.requestedByName || request.requestedByEmail}</Field>
            <Field label={t(`${PREFIX}.fields.requestedAt`)}>{formatDateTimeLocale(asUtc(request.requestedAt))}</Field>
            {request.requestedByName && request.requestedByEmail && (
              <Field label={t(`${PREFIX}.fields.email`)}>
                <bdi dir="ltr">{request.requestedByEmail}</bdi>
              </Field>
            )}
          </div>
        </section>
      </div>
    </Blade>
  );
};

export default CoordinatorAddRequestBlade;
